'use client';

import { Badge, ToggleSwitch } from '@web/ui';

import { DeliverableConfidenceControl } from './DeliverableConfidenceControl';
import { DeliverableDueDateControl } from './DeliverableDueDateControl';
import { DeliverableStatusControl } from './DeliverableStatusControl';
import { DeliverableTypeControl } from './DeliverableTypeControl';
import styles from './JobTasksComponents.module.scss';

import type { JobDeliverable } from '@/features/jobs/types';
import type { ReactNode } from 'react';

type DeliverableTypeOption = {
  id: string;
  label: string;
};

type DeliverableMetricsStripProps = {
  jobId?: string;
  deliverable: JobDeliverable;
  readOnly: boolean;
  showDueDate?: boolean;
  onPatched?: (patch: Partial<JobDeliverable>) => void;
  selectedDeliverableType?: DeliverableTypeOption | null;
  onDeliverableTypeChange?: (nextType: DeliverableTypeOption | null) => void;
  hybridHoursLabel?: string;
  hybridSwitchTooltip?: ReactNode;
  isHybridEnabled?: boolean;
  isHybridSwitchDisabled?: boolean;
  onHybridToggle?: (checked: boolean) => void;
};

export function DeliverableMetricsStrip({
  jobId,
  deliverable,
  readOnly,
  showDueDate = true,
  onPatched,
  selectedDeliverableType = null,
  onDeliverableTypeChange,
  hybridHoursLabel,
  hybridSwitchTooltip,
  isHybridEnabled = false,
  isHybridSwitchDisabled = false,
  onHybridToggle,
}: DeliverableMetricsStripProps) {
  return (
    <div
      className={`${styles.deliverableMetricsStrip} ${
        showDueDate ? '' : styles.deliverableMetricsStripCompact
      }`}
    >
      <DeliverableStatusControl
        key={`${deliverable.id}:status:${deliverable.status}`}
        jobId={jobId}
        deliverableId={deliverable.id}
        value={deliverable.status}
        readOnly={readOnly}
        onPatched={onPatched}
      />

      {showDueDate ? (
        <DeliverableDueDateControl
          jobId={jobId}
          deliverableId={deliverable.id}
          value={deliverable.due_date}
          readOnly={readOnly}
          onPatched={onPatched}
        />
      ) : null}

      <DeliverableConfidenceControl
        key={`${deliverable.id}:confidence:${deliverable.progress_confidence ?? 'not-set'}`}
        jobId={jobId}
        deliverableId={deliverable.id}
        value={deliverable.progress_confidence}
        readOnly={readOnly}
        onPatched={onPatched}
      />

      <DeliverableTypeControl
        value={selectedDeliverableType}
        readOnly={readOnly}
        onChange={onDeliverableTypeChange}
      />

      {onHybridToggle ? (
        <div className={styles.hybridStripControl}>
          <ToggleSwitch
            name={`deliverable-hybrid-delivery-${deliverable.id}`}
            checked={isHybridEnabled}
            disabled={isHybridSwitchDisabled}
            tooltip={hybridSwitchTooltip}
            onChange={onHybridToggle}
          />
          {isHybridEnabled && hybridHoursLabel ? (
            <Badge tone="success" size="lg" label={hybridHoursLabel} />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
